'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/ml-extra'

const TreeViz = dynamic(() => import('../_components/widgets/MLExtraWidgets').then(m => m.TreeViz), { ssr: false })

export default function DecisionTrees() {
  return (
    <Lesson>
      <Step>
        <H>Twenty questions</H>
        <P>
          A <strong>decision tree</strong> classifies by asking a series of yes/no questions about one feature at a time: “Is x
          below 0.4? If so, is y above 1.2?…” Each answer narrows things down until a leaf gives the verdict. In a picture, every
          question cuts the space with a straight, axis-aligned line, and the leaves are boxes.
        </P>
        <Figure n="6.9" wide caption="Grow the tree one level at a time. Each level adds questions and finer boxes. The rules on the right are the whole model, readable by a person.">
          <TreeViz />
        </Figure>
        <Callout kind="try">
          Push the depth up to 10. Training accuracy climbs towards 100%. What happens to test accuracy? Where is it highest?
        </Callout>
      </Step>

      <Step gate={['q-gini', 'gini-split']}>
        <H>Which question to ask?</H>
        <P>
          A good question splits a mixed group into purer groups. To measure “mixed”, trees use <strong>Gini impurity</strong>:
          the chance that two random picks from the group have different labels.
        </P>
        <MB>{'G = 1 - \\sum_k p_k^2'}</MB>
        <P>
          A pure group has <M>{'G = 0'}</M>; a 50/50 group of two labels has <M>{'G = 0.5'}</M>. The tree tries every feature
          and every threshold, and picks the one whose two sides have the lowest Gini (weighted by size). Then it repeats on
          each side.
        </P>
        <Quiz
          id="q-gini"
          question="A group has 8 cats and 2 dogs. What is its Gini impurity?"
          options={[
            { label: '1 − (0.8² + 0.2²) = 0.32', correct: true },
            { label: '0.2', why: 'That’s the fraction of dogs. Gini is 1 minus the sum of squared fractions.' },
            { label: '0.5', why: 'That would be an even 50/50 split. This group is mostly cats, so it’s purer.' },
          ]}
        />
        <Exercise
          id="gini-split"
          title="gini and bestSplit"
          {...EX.giniSplit}
          hints={[
            <>
              gini: count each label in a Map, then <C>{'g -= (count / labels.length) ** 2'}</C> starting from 1.
            </>,
            <>
              bestSplit: for each feature, take <C>{'[...new Set(X.map(x => x[f]))].sort((a, b) => a - b)'}</C>, try thresholds
              halfway between neighbours, and score each split.
            </>,
          ]}
        />
      </Step>

      <Step gate="build-tree">
        <H>Grow it recursively</H>
        <P>
          A tree is a recursive structure: each split holds two smaller trees. So building one is a recursive function, just
          like the tree-walking you wrote in Module 1.
        </P>
        <Exercise
          id="build-tree"
          title="build and predict"
          {...EX.buildTree}
          hints={[
            <>
              Leaf cases first: <C>{'if (depth === 0 || gini(y) === 0) return { leaf: true, label: majority(y) }'}</C>. Then
              find <C>bestSplit</C>; if it doesn’t lower the Gini, make a leaf too.
            </>,
            <>
              Collect the indices on each side with <C>filter</C>, and call <C>build</C> on each with <C>depth - 1</C>.
            </>,
            <>
              predict: <C>{'if (tree.leaf) return tree.label'}</C>; otherwise recurse into <C>left</C> or <C>right</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-forest">
        <H>Many trees are better than one</H>
        <P>
          A single deep tree overfits: it memorises noise in tiny boxes. The fix is to grow <strong>many</strong> trees, each a
          little different, and let them vote.
        </P>
        <UL>
          <li>
            <strong>Random forests</strong> train each tree on a random resample of the data and let it use only random subsets
            of features. Their individual mistakes are different, so averaging cancels them out.
          </li>
          <li>
            <strong>Gradient boosting</strong> grows trees one after another, each trained to fix the errors left by the trees
            so far. It’s gradient descent, where each step adds a tree.
          </li>
        </UL>
        <Quiz
          id="q-forest"
          question="Why does averaging many overfitted trees help?"
          options={[
            { label: 'Each tree’s noise-chasing errors point in different directions, so they cancel when averaged', correct: true },
            { label: 'Each tree becomes simpler', why: 'Each tree can still be deep. The averaging is what smooths things out.' },
            { label: 'It doesn’t — the forest overfits just as much', why: 'Try it in the stretch goal of the house-price project. Forests are famously hard to overfit.' },
          ]}
          explain="Combining many models like this is called an ensemble. For tables of data (rather than images or text), tree ensembles such as gradient boosting are still often the best tool there is."
        />
      </Step>
    </Lesson>
  )
}
